import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Link, NavLink } from "@/components/LocaleLink";
import { Menu, X, Phone } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { CONTACT } from "@/i18n/translations";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const Navbar = () => {
  const { t, lang, toggleLang } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const links = [
    { to: "/", label: t.nav.home },
    { to: "/ydelser", label: t.nav.ydelser },
    { to: "/indsatser", label: t.nav.indsatser },
    { to: "/kommuner-og-kriminalforsorgen", label: t.nav.kommuner },
    { to: "/akut-projekter", label: t.nav.akut },
    { to: "/udsatte", label: t.nav.udsatte },
    { to: "/adhd", label: t.nav.adhd },
    { to: "/autisme", label: t.nav.autisme },
  ];

  const lightTopRoutes = ["/find-forloeb"];
  const onLight = scrolled || lightTopRoutes.includes(location.pathname); // dark text on white/scrolled header

  const linkClass = ({ isActive }) =>
    `text-sm font-medium transition-colors duration-300 ${
      onLight
        ? isActive
          ? "text-[#2D5A3F]"
          : "text-[#3B5245] hover:text-[#2D5A3F]"
        : isActive
        ? "text-white"
        : "text-white/80 hover:text-white"
    }`;

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-white/80 backdrop-blur-xl border-b border-black/5" : "bg-transparent"
      }`}
      data-testid="main-navbar"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2.5 group" data-testid="nav-logo">
          <img
            src={onLight ? "/assets/lotus-green.png" : "/assets/lotus-white.png"}
            alt="Lotusgruppen"
            className="h-11 w-11 object-contain transition-transform duration-300 group-hover:scale-105"
          />
          <span className={`font-display text-2xl leading-none tracking-tight transition-colors duration-300 ${onLight ? "text-[#1A2E24]" : "text-white"}`}>
            Lotusgruppen
          </span>
        </Link>

        <nav className="hidden xl:flex items-center gap-4 2xl:gap-5" aria-label="Primary">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} className={linkClass} data-testid={`nav-link-${l.to === "/" ? "home" : l.to.slice(1)}`}>
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={toggleLang}
            className={`hidden sm:flex items-center gap-1 text-xs font-semibold uppercase tracking-widest rounded-full px-3 py-1.5 transition-colors duration-300 ${onLight ? "text-[#3B5245] border border-[#D3CEC0] hover:border-[#2D5A3F] hover:text-[#2D5A3F]" : "text-white border border-white/40 hover:border-white"}`}
            data-testid="lang-toggle"
            aria-label={t.common.langLabel}
          >
            <span className={lang === "da" ? "opacity-100" : "opacity-50"}>DA</span>
            <span className="opacity-40">/</span>
            <span className={lang === "en" ? "opacity-100" : "opacity-50"}>EN</span>
          </button>

          <Link
            to="/kontakt"
            className={`hidden md:inline-flex xl:hidden 2xl:inline-flex items-center rounded-full text-sm font-medium px-5 py-2.5 transition-colors duration-300 ${onLight ? "bg-[#2D5A3F] text-white hover:bg-[#1A2E24]" : "bg-white text-[#1A2E24] hover:bg-[#EAE5D9]"}`}
            data-testid="nav-cta-contact"
          >
            {t.nav.cta}
          </Link>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button className={`xl:hidden flex items-center justify-center h-11 w-11 rounded-full border ${onLight ? "border-[#D3CEC0] text-[#1A2E24]" : "border-white/40 text-white"}`} data-testid="mobile-menu-trigger" aria-label="Menu">
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-[#F7F5F0] border-l border-black/5 w-[85vw] sm:w-[380px] p-0">
              <div className="flex flex-col h-full p-7">
                <div className="flex items-center justify-between mb-10">
                  <span className="flex items-center gap-2.5">
                    <img src="/assets/lotus-green.png" alt="Lotusgruppen" className="h-10 w-10 object-contain" />
                    <span className="font-display text-2xl text-[#1A2E24]">Lotusgruppen</span>
                  </span>
                  <button onClick={() => setOpen(false)} className="h-10 w-10 flex items-center justify-center rounded-full border border-[#D3CEC0]" data-testid="mobile-menu-close" aria-label="Close">
                    <X className="h-5 w-5" />
                  </button>
                </div>
                <nav className="flex flex-col gap-1" aria-label="Mobile">
                  {[...links, { to: "/kontakt", label: t.nav.kontakt }].map((l) => (
                    <NavLink
                      key={l.to}
                      to={l.to}
                      className={({ isActive }) =>
                        `font-display text-2xl py-2.5 border-b border-[#D3CEC0]/60 transition-colors ${
                          isActive ? "text-[#2D5A3F]" : "text-[#1A2E24]"
                        }`
                      }
                      data-testid={`mobile-nav-${l.to === "/" ? "home" : l.to.slice(1)}`}
                    >
                      {l.label}
                    </NavLink>
                  ))}
                </nav>
                <button
                  onClick={toggleLang}
                  className="mt-8 self-start flex items-center gap-2 text-sm font-semibold uppercase tracking-widest text-[#3B5245] border border-[#D3CEC0] rounded-full px-4 py-2"
                  data-testid="mobile-lang-toggle"
                >
                  <span className={lang === "da" ? "text-[#2D5A3F]" : "opacity-50"}>DA</span>
                  <span className="opacity-30">/</span>
                  <span className={lang === "en" ? "text-[#2D5A3F]" : "opacity-50"}>EN</span>
                </button>
                <a href={`tel:${CONTACT.phoneRaw}`} className="mt-auto flex items-center gap-3 text-[#2D5A3F] font-medium" data-testid="mobile-phone">
                  <Phone className="h-4 w-4" /> {CONTACT.phone}
                </a>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
